<script setup lang="ts">
// A bar chart of one or more count series, vertical or horizontal. Every
// category bar in the dashboard is this component: horizontal where the labels
// are words (product, applicator, species names) so they stay readable, and the
// caller supplies a colour per bar so a bucket that cannot be drilled into can
// read muted beside the ones that can.
//
// A caller may also overlay line datasets — a range band or its mean — on the
// same axes. That is what keeps the trajectory one chart: the bars say what
// this season did each week, the band says what the same fit found in the five
// seasons before it, and both read against one scale.
import { computed } from "vue";
import { Bar } from "vue-chartjs";
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Filler,
	Legend,
	LineController,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from "chart.js";
import type { ChartData, ChartOptions, Plugin, TooltipItem } from "chart.js";

import {
	barTooltipLabel,
	type BarDataset,
	type BarOverlayDataset,
	GRID_COLOR,
	legendOptions,
	TICK_COLOR,
	TICK_FONT,
} from "./theme";

// Register only what this chart draws with. vue-chartjs's `Bar` registers the
// bar controller itself; naming it here too keeps this wrapper correct on its
// own terms, and chart.js's registry is idempotent.
ChartJS.register(
	BarController,
	BarElement,
	CategoryScale,
	LinearScale,
	LineController,
	LineElement,
	PointElement,
	Filler,
	Tooltip,
	Legend,
);

const props = defineProps<{
	labels: string[];
	/** The bars, plus any line overlay the caller draws on the same axes. A
	 * gap is null, never zero: an absent reading has no bar. */
	datasets: (BarDataset | BarOverlayDataset)[];
	horizontal?: boolean;
	/** The preferred-unit name for the areas a dataset may carry, so the
	 * tooltip can name the unit rather than leaving a bare number. */
	areaUnit?: string;
	/** Draw the axis up from zero and mark it with a dashed rule. A series that
	 * runs negative (a growth rate) still reads: the bars fall below the rule
	 * rather than being clipped against the frame. */
	zeroBaseline?: boolean;
	/** A unit the value axis and the tooltip read in, appended to each value. */
	tickSuffix?: string;
	/** Draw the built-in legend. Defaults to "when there is more than one
	 * series"; a panel that names its series in its own key turns it off rather
	 * than printing two legends. */
	showLegend?: boolean;
}>();

// Click-to-filter: the panel maps the clicked bar's index back to its bucket
// and turns that bucket's key into a spec clause. The wrapper knows nothing
// about clauses — it only reports which mark was hit.
const emit = defineEmits<{ select: [index: number] }>();

const chartData = computed<ChartData<"bar">>(() => ({
	labels: props.labels,
	// The overlay lines are line datasets on a bar chart's axes. Chart.js draws
	// them; the compiled types only describe a chart of one controller, so the
	// mix is stated here once rather than leaked to every caller.
	datasets: props.datasets as unknown as ChartData<"bar">["datasets"],
}));

/** The dashed zero rule. Chart.js has no per-gridline dash, so the one line the
 * sign reads from is drawn over the plot area itself. */
const zeroRule: Plugin<"bar"> = {
	id: "zeroRule",
	beforeDatasetsDraw(chart) {
		const scale = chart.scales.y;
		if (!scale) return;
		const y = scale.getPixelForValue(0);
		const area = chart.chartArea;
		if (y < area.top || y > area.bottom) return;
		const { ctx } = chart;
		ctx.save();
		ctx.setLineDash([4, 3]);
		ctx.lineWidth = 1;
		ctx.strokeStyle = TICK_COLOR;
		ctx.beginPath();
		ctx.moveTo(area.left, y);
		ctx.lineTo(area.right, y);
		ctx.stroke();
		ctx.restore();
	},
};

const chartOptions = computed<ChartOptions<"bar">>(() => {
	const horizontal = props.horizontal === true;
	const suffix = props.tickSuffix ?? "";
	// The value axis is always the count axis here, so it takes the whole-number
	// ticks; the category axis carries the words and no gridlines, which would
	// only be lines between labels.
	const valueTicks = {
		color: TICK_COLOR,
		font: TICK_FONT,
		precision: 0,
		callback: (value: string | number) =>
			`${Number(value).toLocaleString()}${suffix}`,
	};
	const categoryTicks = { color: TICK_COLOR, font: TICK_FONT, autoSkip: true };
	const valueAxis = {
		beginAtZero: true,
		grid: { color: GRID_COLOR },
		ticks: valueTicks,
	};
	return {
		responsive: true,
		maintainAspectRatio: false,
		indexAxis: horizontal ? "y" : "x",
		interaction: { mode: "nearest", intersect: true },
		onClick: (_event, elements) => {
			if (elements.length === 0) return;
			emit("select", elements[0].index);
		},
		plugins: {
			// The axis labels name single-series bars; a legend is only needed
			// once a second series has to be told from the first.
			legend: legendOptions(props.showLegend ?? props.datasets.length > 1),
			tooltip: {
				// A band's edges are furniture: they are drawn to show the range,
				// not to be read as two more weekly values.
				filter: (item) => !(item.dataset as BarOverlayDataset).band,
				callbacks: {
					label: (context: TooltipItem<"bar">) =>
						barTooltipLabel(context, horizontal, props.areaUnit, suffix),
				},
			},
		},
		scales: {
			x: horizontal
				? valueAxis
				: { grid: { display: false }, ticks: categoryTicks },
			y: horizontal
				? { grid: { display: false }, ticks: categoryTicks }
				: valueAxis,
		},
	};
});
</script>

<template>
	<div class="chart-box">
		<Bar
			:data="chartData"
			:options="chartOptions"
			:plugins="zeroBaseline ? [zeroRule] : []"
		/>
	</div>
</template>

<style scoped>
/* chart.js with maintainAspectRatio:false sizes itself to this box, so the box
   — not the canvas — is what the panel's layout controls. */
/* The panel owns the height; the box fills it, so a taller grid row grows the
   chart rather than leaving a gap under it. */
.chart-box {
	position: absolute;
	inset: 0;
}
</style>
